// Cursor pagination of `/s` over Search V2. Search V2 continues with opaque cursors, so `/s` keeps
// the pages it has visited plus at most one next page; it never invents pages it has not reached.
//
//   pages = [{ listings, nextCursor }, ...]   pageIndex = the page on screen
//
// A new search (query, filters or sort: the request key) clears the cache and loads page 1. Every
// response carries the identity of the search that issued it and is dropped when that search is no
// longer active. Cursors and page numbers never enter the URL.
import { useEffect, useReducer, useRef } from 'react';

import { searchListingsV2 } from '../../util/api';
import {
  brandValuesFromFacets,
  searchV2CardToListing,
  searchV2RequestKey,
} from './searchV2Adapter';

// An empty page with a continuation means the response gate rejected every candidate of that
// stretch; follow it a few times before showing an empty page.
export const MAX_EMPTY_CONTINUATIONS = 3;
// Continuation errors that restart the same search from page 1.
export const CURSOR_RESET_CODES = ['cursor_expired', 'cursor_context_mismatch', 'cursor_invalid'];

export const searchV2ErrorCode = error => error?.error?.code || null;

/** One page: the first page also asks for the brand facet. Throws the API error unchanged. */
export const loadSearchV2Page = async (search, request, cursor) => {
  const call = async body => {
    const response = await search(body);
    if (response?.error) throw response;
    return response;
  };
  let response = await call(cursor ? { ...request, cursor } : { ...request, facets: ['brand'] });
  const facets = cursor ? null : response.approximate?.facets || null;
  for (let hops = 0; hops < MAX_EMPTY_CONTINUATIONS; hops += 1) {
    const next = response.continuation?.next_cursor;
    if (response.items.length > 0 || !next) break;
    response = await call({ ...request, cursor: next });
  }
  return {
    listings: response.items.map(searchV2CardToListing),
    nextCursor: response.continuation?.next_cursor || null,
    facets,
  };
};

export const initialSearchV2State = {
  requestId: 0,
  pages: [],
  pageIndex: 0,
  loading: false,
  error: null,
  brandValues: null,
};

export const searchV2PagesReducer = (state, action) => {
  if (action.type === 'reset') {
    // Brand values stay until the new first page replaces them, as the legacy loader did.
    return {
      ...initialSearchV2State,
      requestId: action.requestId,
      loading: true,
      brandValues: state.brandValues,
    };
  }
  if (action.requestId !== state.requestId) return state; // a response of an inactive search
  switch (action.type) {
    case 'loading':
      return { ...state, loading: true, error: null };
    case 'loaded': {
      if (action.index > state.pages.length) return state;
      const { facets, ...page } = action.page;
      return {
        ...state,
        pages: [...state.pages.slice(0, action.index), page],
        pageIndex: action.index,
        loading: false,
        error: null,
        brandValues: action.index === 0 ? brandValuesFromFacets(facets) : state.brandValues,
      };
    }
    case 'failed':
      return {
        ...state,
        loading: false,
        error: { code: action.code, index: action.index },
        brandValues: action.index === 0 ? null : state.brandValues,
      };
    case 'show':
      return action.index < state.pages.length
        ? { ...state, pageIndex: action.index, error: null }
        : state;
    default:
      return state;
  }
};

/**
 * `/s` Search V2 results for `request` (inactive when `enabled` is false). Returns the state plus
 * `goToPage(index)` (0-based: a visited page from the cache, or the next page by its cursor) and
 * `retry()`.
 */
export const useSearchV2Pages = (request, enabled, search = searchListingsV2) => {
  const key = enabled ? searchV2RequestKey(request) : null;
  const [state, dispatch] = useReducer(searchV2PagesReducer, initialSearchV2State);
  const stateRef = useRef(state);
  stateRef.current = state;
  const requestRef = useRef(request);
  requestRef.current = request;
  const activeIdRef = useRef(0);

  const fetchPage = (requestId, index, cursor) => {
    dispatch({ type: 'loading', requestId });
    loadSearchV2Page(search, requestRef.current, cursor).then(
      page => {
        if (activeIdRef.current === requestId) dispatch({ type: 'loaded', requestId, index, page });
      },
      error => {
        if (activeIdRef.current !== requestId) return;
        const code = searchV2ErrorCode(error);
        if (cursor && CURSOR_RESET_CODES.includes(code)) {
          // eslint-disable-next-line no-use-before-define
          startSearch();
        } else {
          dispatch({ type: 'failed', requestId, index, code });
        }
      }
    );
  };

  const startSearch = () => {
    activeIdRef.current += 1;
    const requestId = activeIdRef.current;
    dispatch({ type: 'reset', requestId });
    fetchPage(requestId, 0, null);
  };

  useEffect(() => {
    if (key === null) {
      activeIdRef.current += 1; // drop whatever is still in flight
      return;
    }
    startSearch();
  }, [key]);

  const goToPage = index => {
    const s = stateRef.current;
    if (s.loading || index < 0) return;
    if (index < s.pages.length) {
      dispatch({ type: 'show', requestId: s.requestId, index });
      return;
    }
    const cursor = s.pages[s.pages.length - 1]?.nextCursor;
    if (index === s.pages.length && cursor) fetchPage(s.requestId, index, cursor);
  };

  const retry = () => {
    const s = stateRef.current;
    if (!s.error || s.loading) return;
    const { index } = s.error;
    fetchPage(s.requestId, index, index === 0 ? null : s.pages[index - 1]?.nextCursor || null);
  };

  return { ...state, goToPage, retry };
};
